import React, { useEffect, useState } from 'react';
import { Flex, Text } from '../design-system';
import { FaCrosshairs } from 'react-icons/fa';
import PreLineText from './PreLineText';
import { isExpired, formatTime, fieldFormat } from '../helpers/utils';
import { ExpiredText } from './Expired';

const CrossHairs = () => (
  <Text mr={1}>
    <FaCrosshairs />
  </Text>
);

interface FieldTextProps {
  label: string;
  name: string;
  type?: string;
  transformOnBlur?: (value: any) => React.ReactNode;
  data: Record<string, any>;
}

const FieldText = ({
  label,
  name,
  type,
  transformOnBlur,
  data,
}: FieldTextProps) => {
  const [text, setText] = useState<any>(false);

  const hyperlinks = (inputString: string, value = '') => {
    if (value) {
      const x = `<a style="color: blue;" target="_blank" href="${inputString}">${value}</a>`;
      return x;
    } else {
      return inputString.replace(
        /(https?:\/\/\S+)/g,
        '<a style="color: blue;" target="_blank" href="$1">$1</a>',
      );
    }
  };

  useEffect(() => {
    let fieldVal = null;
    // Temporary fix. TODO: Add a new datapoint to PROJECT_FIELDS that represents where the data points to
    // Should also try to get rid of whatever GeneralInformationFields.js is trying to do.
    if (name === 'funded_program') {
      // check that the funded program actually exists, and that it hasn't been deleted from the vault.
      if (
        data.funded_program === null ||
        data.client.programs.find(
          (prg: any) => prg.id === data.funded_program,
        ) === undefined
      ) {
        return;
      }
      fieldVal = data['client'];
    } else {
      fieldVal = data[name];
    }

    if (fieldVal && typeof fieldVal === 'string' && fieldVal.includes('http')) {
      setText(hyperlinks(fieldVal));
    } else if (name === 'tax_id' && fieldVal) {
      setText(
        hyperlinks(
          `https://projects.propublica.org/nonprofits/organizations/${fieldVal}`,
          fieldVal,
        ),
      );
    } else if (name === 'funder' && fieldVal) {
      setText(hyperlinks(`/funders/edit/${fieldVal.id}`, fieldVal.name));
    } else if (name === 'client' && fieldVal) {
      setText(hyperlinks(`/clients/edit/${fieldVal.id}`, fieldVal.name));
    } else if (name === 'funded_program' && data.funded_program) {
      const progName = data.client.programs.find(
        (prg: any) => prg.id === data.funded_program,
      ).name;
      setText(
        hyperlinks(
          `/vault/${fieldVal.id}/program-case-statements/${data.funded_program}`,
          progName,
        ),
      );
    } else {
      setText(fieldVal);
    }
  }, [data, name]);

  return type === 'date' ? (
    <ExpiredText expired={name === 'expires' && isExpired(data.expires)}>
      <Flex flexDirection="column">
        <Text pb={1} fontWeight={700}>
          {label}
        </Text>
        <Flex>
          <Text>
            {data.hard_deadline && name === 'deadline' && <CrossHairs />}
          </Text>
          <Text id={fieldFormat(name, '-field')}>
            {text && formatTime(text)}
          </Text>
        </Flex>
      </Flex>
    </ExpiredText>
  ) : (
    <Flex flexDirection="column">
      <Text pb={1} fontWeight={700}>
        {label}
      </Text>
      <span>
        {text &&
        transformOnBlur &&
        name !== 'tax_id' &&
        name !== 'funder' &&
        name !== 'client' &&
        name !== 'funded_program' ? (
          <PreLineText id={fieldFormat(name, '-field')}>
            {transformOnBlur(text)}
          </PreLineText>
        ) : (
          <div
            id={fieldFormat(name, '-field')}
            dangerouslySetInnerHTML={{
              __html: text ? text.replace(/\n/g, '<br />') : '',
            }}
          />
        )}
      </span>
    </Flex>
  );
};

export default FieldText;
